import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageSquare, Send, Users, Reply, X } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { IMAGES, bgStyle } from '@/config/images';

const Community = () => {
  const { user } = useContext(AuthContext);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const token = localStorage.getItem('token');

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    fetchRooms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoom]);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/community/rooms`);
      setRooms(response.data);
      if (response.data.length > 0) {
        setSelectedRoom(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMessages = async (roomId) => {
    try {
      const response = await axios.get(`${API}/community/rooms/${roomId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!selectedRoom) return;

    if (editingMessage) {
      await handleSaveEdit();
      return;
    }

    if (!newMessage.trim()) return;

    try {
      await axios.post(`${API}/community/rooms/${selectedRoom.id}/messages`, {
        message: newMessage,
        parent_id: replyingTo?.id || null
      });
      setNewMessage('');
      setReplyingTo(null);
      fetchMessages(selectedRoom.id);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleReply = (msg) => {
    setReplyingTo(msg);
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Group messages: top-level messages and their replies
  const getThreadedMessages = () => {
    const topLevel = messages.filter(m => !m.parent_id);
    const replies = messages.filter(m => m.parent_id);
    
    return topLevel.map(msg => ({
      ...msg,
      replies: replies.filter(r => r.parent_id === msg.id)
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingMessage || !token || !selectedRoom) return;
    try {
      await axios.put(
        `${API}/community/messages/${editingMessage.id}`,
        { message: editText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchMessages(selectedRoom.id);
      setEditingMessage(null);
      setEditText('');
    } catch (e) {
      toast.error('Failed to edit message');
    }
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    setEditText('');
  };


  return (
    <div 
      data-testid="community-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.COMMUNITY_FORUM)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="max-w-6xl mx-auto space-y-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">CONNECT</div>
            <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tight text-[#e67e22] mb-2">
              COMMUNITY CHAT
            </h1>
            <p className="text-lg text-[#e67e22]">Connect with CNC makers worldwide</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Room List */}
            <Card className="lg:col-span-1 bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#e67e22]" />
                  Chat Rooms
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {rooms.map((room) => (
                  <div
                    key={room.id}
                    data-testid={`room-${room.id}`}
                    onClick={() => setSelectedRoom(room)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedRoom?.id === room.id
                        ? 'bg-[#e67e22] text-black'
                        : 'bg-black/50 text-white hover:bg-black/70 border border-[#3a3a3a]'
                    }`}
                  >
                    <div className="font-bold text-sm">{room.name}</div>
                    <div className={`text-xs ${selectedRoom?.id === room.id ? 'text-black/70' : 'text-[#a3a3a3]'}`}>
                      {room.category}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Chat Area */}
            <Card className="lg:col-span-3 bg-black/60 backdrop-blur-sm border-[#3a3a3a]">
              {selectedRoom ? (
                <>
                  <CardHeader>
                    <CardTitle className="text-white">{selectedRoom.name}</CardTitle>
                    <CardDescription className="text-[#a3a3a3]">{selectedRoom.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ScrollArea data-testid="messages-area" className="h-[300px] sm:h-[400px] w-full rounded-lg border border-[#3a3a3a] bg-black/50 p-4">
                      <div className="space-y-4">
                        {getThreadedMessages().map((msg) => (
                          <div key={msg.id} className="space-y-2">
                            {/* Main message */}
                            <div
                              data-testid={`message-${msg.id}`}
                              className={`p-3 rounded-lg ${
                                msg.user_id === user.id ? 'bg-[#e67e22]/20 border border-[#e67e22]/30' : 'bg-black/50 border border-[#3a3a3a]'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <div className="text-xs text-[#e67e22] font-bold">{msg.user_name}</div>
                                <div className="flex items-center gap-2">
                                  {msg.user_id === user.id && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setEditingMessage({ id: msg.id, message: msg.message });
                                        setEditText(msg.message);
                                      }}
                                      className="h-6 px-2 text-xs text-[#a3a3a3] hover:text-[#e67e22] hover:bg-transparent"
                                    >
                                      Edit
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleReply(msg)}
                                    className="h-6 px-2 text-xs text-[#a3a3a3] hover:text-[#e67e22] hover:bg-transparent"
                                  >
                                    <Reply className="w-3 h-3 mr-1" />
                                    Reply
                                  </Button>
                                </div>
                              </div>
                              <div className="text-white">{msg.message}</div>
                              <div className="text-xs text-[#a3a3a3] mt-1">
                                {new Date(msg.timestamp).toLocaleString()}
                              </div>
                            </div>
                            
                            {/* Replies */}
                            {msg.replies && msg.replies.length > 0 && (
                              <div className="ml-6 space-y-2 border-l-2 border-[#e67e22]/30 pl-3">
                                {msg.replies.map((reply) => (
                                  <div
                                    key={reply.id}
                                    data-testid={`reply-${reply.id}`}
                                    className={`p-2 rounded-lg text-sm ${
                                      reply.user_id === user.id ? 'bg-[#e67e22]/10 border border-[#e67e22]/20' : 'bg-black/70 border border-[#3a3a3a]/50'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="text-xs text-[#10B981] font-bold">{reply.user_name}</div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleReply(msg)}
                                        className="h-5 px-1 text-xs text-[#a3a3a3] hover:text-[#e67e22] hover:bg-transparent"
                                      >
                                        <Reply className="w-3 h-3" />
                                      </Button>
                                    </div>
                                    <div className="text-white/90">{reply.message}</div>
                                    <div className="text-xs text-[#a3a3a3] mt-1">
                                      {new Date(reply.timestamp).toLocaleString()}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>

                    {/* Reply indicator */}
                    {replyingTo && (
                      <div className="flex items-center gap-2 p-2 bg-[#e67e22]/10 rounded-lg border border-[#e67e22]/30">
                        <Reply className="w-4 h-4 text-[#e67e22]" />
                        <span className="text-sm text-[#d6d6d6] flex-1">
                          Replying to <span className="text-[#e67e22] font-bold">{replyingTo.user_name}</span>: {replyingTo.message.substring(0, 50)}...
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={cancelReply}
                          className="h-6 w-6 p-0 text-[#a3a3a3] hover:text-red-400 hover:bg-transparent"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}

                    {editingMessage && (
                      <div className="flex items-center gap-2 p-2 bg-black/50 border border-[#3a3a3a]">
                        <span className="text-sm text-[#d6d6d6] flex-1">Editing your message…</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={cancelEdit}
                          className="h-6 px-2 text-xs text-[#a3a3a3] hover:text-red-400 hover:bg-transparent"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleSaveEdit}
                          className="h-6 px-2 text-xs text-[#a3a3a3] hover:text-[#e67e22] hover:bg-transparent"
                        >
                          Save
                        </Button>
                      </div>
                    )}

                    <form onSubmit={handleSendMessage} className="flex gap-2">
                      <Input
                        data-testid="message-input"
                        type="text"
                        placeholder={editingMessage ? 'Edit your message…' : (replyingTo ? `Reply to ${replyingTo.user_name}...` : 'Type your message...')}
                        value={editingMessage ? editText : newMessage}
                        onChange={(e) => (editingMessage ? setEditText(e.target.value) : setNewMessage(e.target.value))}
                        className="bg-black/50 border-[#3a3a3a] text-white placeholder:text-[#a3a3a3] flex-1"
                      />
                      <Button
                        data-testid="send-message-btn"
                        type="submit"
                        className="bg-[#10B981] hover:bg-[#10B981]/90 text-black font-bold"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </form>
                  </CardContent>
                </>
              ) : (
                <div className="flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 text-[#a3a3a3] mx-auto mb-4" />
                    <p className="text-[#a3a3a3]">Select a room to start chatting</p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
    </div>
  );
};

export default Community;
